import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { Menu, ShoppingBag, User, X } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const LINKS = [
  { to: "/methode", label: "La méthode", testid: "nav-methode-link" },
  { to: "/guides", label: "Les guides", testid: "nav-guides-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const location = useLocation();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <motion.header
      className={`nav ${scrolled ? "scrolled" : ""}`}
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
      data-testid="main-nav"
    >
      <button
        className="logo"
        onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
        data-testid="nav-logo"
        aria-label="SamNara — retour en haut"
      >
        <img src="/logo-samnara.png" alt="SamNara" className="logo-img" />
      </button>
      <nav className="nav-links">
        {LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="nav-text-link" data-testid={l.testid}>
            {l.label}
          </Link>
        ))}
        <Link to="/espace" className="nav-cart" aria-label="Espace membre" data-testid="nav-account-link">
          <User size={18} />
        </Link>
        <a
          href="https://samnara.myshopify.com/cart"
          className="nav-cart"
          aria-label="Panier"
          data-testid="nav-cart-link"
        >
          <ShoppingBag size={18} />
        </a>
        <button
          type="button"
          className="nav-burger"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          data-testid="nav-burger-btn"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.35, ease: EASE }}
            data-testid="mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -22 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + i * 0.07, duration: 0.45, ease: EASE }}
              >
                <Link to={l.to} className="mobile-link" data-testid={`mobile-${l.testid}`} onClick={() => setOpen(false)}>
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <motion.div
              initial={{ opacity: 0, x: -22 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.08 + LINKS.length * 0.07, duration: 0.45, ease: EASE }}
            >
              <Link
                to="/espace"
                className="mobile-link mobile-link-accent"
                data-testid="mobile-nav-account-link"
                onClick={() => setOpen(false)}
              >
                Mon espace
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
